import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  Video,
  Plus,
  Users,
  Clock,
  Play,
  Calendar,
  Link2,
  Copy,
  Check,
  Shield,
  Sparkles,
  PhoneCall,
  History,
  Settings,
  ExternalLink,
  MoreVertical,
  Trash2
} from 'lucide-react';
import CreateRoomModal from '../components/visage/CreateRoomModal';
import MeetingLobby from '../components/visage/MeetingLobby';
import VideoRoom from '../components/visage/VideoRoom';

const Visage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showLobby, setShowLobby] = useState(false);
  const [copiedRoomId, setCopiedRoomId] = useState(null);
  const [quickJoinCode, setQuickJoinCode] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [pastRooms, setPastRooms] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    loadRooms();
    // Poll for room updates
    const interval = setInterval(loadRooms, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle ?join=roomId from URL (e.g. from Sigil video button or shared link)
  useEffect(() => {
    const joinId = searchParams.get('join');
    if (joinId && rooms.length > 0) {
      const room = rooms.find(r => r.id.toString() === joinId);
      if (room) {
        setSelectedRoom(room);
        setShowLobby(true);
        setSearchParams({});
      }
    }
  }, [rooms, searchParams]);

  const loadRooms = async () => {
    try {
      const response = await api.get('/visage/rooms');
      setRooms(response.data);
    } catch (error) {
      console.error('Error loading rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPastRooms = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get('/visage/rooms/history');
      setPastRooms(response.data);
    } catch (error) {
      console.error('Error loading past rooms:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCreateRoom = async (roomData) => {
    try {
      const response = await api.post('/visage/rooms', roomData);
      setShowCreateRoom(false);
      setSelectedRoom(response.data);
      setShowLobby(true);
      loadRooms();
    } catch (error) {
      console.error('Error creating room:', error);
      alert('Failed to create room');
    }
  };

  const handleJoinRoom = (room) => {
    setSelectedRoom(room);
    setShowLobby(true);
  };

  const handleEnterRoom = () => {
    setShowLobby(false);
  };

  const handleLeaveRoom = () => {
    setSelectedRoom(null);
    setShowLobby(false);
    loadRooms();
  };

  const handleDeleteRoom = async (roomId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to end this meeting?')) return;

    try {
      await api.delete(`/visage/rooms/${roomId}`);
      loadRooms();
    } catch (error) {
      console.error('Error deleting room:', error);
    }
  };

  const copyMeetingLink = (room, e) => {
    e.stopPropagation();
    const link = `${window.location.origin}/visage?join=${room.id}`;
    navigator.clipboard.writeText(link);
    setCopiedRoomId(room.id);
    setTimeout(() => setCopiedRoomId(null), 2000);
  };

  const handleQuickJoin = () => {
    if (quickJoinCode.trim()) {
      // Try to find room by ID or join code
      const room = rooms.find(r => r.id.toString() === quickJoinCode.trim());
      if (room) {
        handleJoinRoom(room);
      } else {
        alert('Meeting not found');
      }
    }
  };

  // If in lobby, show the pre-meeting screen
  if (showLobby && selectedRoom) {
    return (
      <MeetingLobby
        room={selectedRoom}
        onJoin={handleEnterRoom}
        onCancel={handleLeaveRoom}
      />
    );
  }

  // If in a room, show the video room
  if (selectedRoom && !showLobby) {
    return (
      <VideoRoom
        room={selectedRoom}
        onLeave={handleLeaveRoom}
      />
    );
  }

  const activeRooms = rooms.filter(r => r.participant_count > 0);
  const availableRooms = rooms.filter(r => r.participant_count === 0);

  return (
    <div className="h-full flex flex-col">
      {/* Header Section */}
      <div className="flex-shrink-0 mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 via-cyan-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-cyan-500/25">
              <Video className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-virgo-gold-400 to-amber-300 bg-clip-text text-transparent">
                Visage
              </h1>
              <p className="text-virgo-teal-300 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Secure, HIPAA-compliant video conferencing
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Quick Join */}
            <div className="flex items-center gap-2 bg-virgo-navy-700 rounded-xl px-4 py-2 border border-virgo-navy-600">
              <input
                type="text"
                value={quickJoinCode}
                onChange={(e) => setQuickJoinCode(e.target.value)}
                placeholder="Enter meeting code"
                className="bg-transparent text-white placeholder-virgo-navy-400 focus:outline-none w-40"
                onKeyPress={(e) => e.key === 'Enter' && handleQuickJoin()}
              />
              <button
                onClick={handleQuickJoin}
                className="text-virgo-teal-400 hover:text-virgo-teal-300 transition"
              >
                <ExternalLink className="w-5 h-5" />
              </button>
            </div>

            <button
              onClick={() => setShowCreateRoom(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white rounded-xl hover:from-cyan-600 hover:to-indigo-700 transition-all shadow-lg shadow-cyan-500/25 font-medium"
            >
              <Plus className="w-5 h-5" />
              New Meeting
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <button
          onClick={() => setShowCreateRoom(true)}
          className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-2xl p-6 border border-virgo-navy-600 hover:border-cyan-500/50 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Video className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="text-white font-semibold">Start Instant Meeting</h3>
              <p className="text-virgo-teal-400 text-sm">Create and join immediately</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setShowCreateRoom(true)}
          className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-2xl p-6 border border-virgo-navy-600 hover:border-teal-500/50 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="text-white font-semibold">Schedule Meeting</h3>
              <p className="text-virgo-teal-400 text-sm">Plan for later</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => { setActiveTab('history'); loadPastRooms(); }}
          className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-2xl p-6 border border-virgo-navy-600 hover:border-amber-500/50 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <History className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="text-white font-semibold">Past Meetings</h3>
              <p className="text-virgo-teal-400 text-sm">View recordings & history</p>
            </div>
          </div>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'active'
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
              : 'text-virgo-teal-400 hover:text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <PhoneCall className="w-4 h-4" />
            Active Now ({activeRooms.length})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('available')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'available'
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
              : 'text-virgo-teal-400 hover:text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            Available Rooms ({availableRooms.length})
          </div>
        </button>
        <button
          onClick={() => { setActiveTab('history'); loadPastRooms(); }}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            activeTab === 'history'
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
              : 'text-virgo-teal-400 hover:text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Past Meetings ({pastRooms.length})
          </div>
        </button>
      </div>

      {/* Room List */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-virgo-teal-300 flex items-center gap-3">
              <div className="w-8 h-8 border-2 border-virgo-teal-500 border-t-transparent rounded-full animate-spin" />
              Loading meetings...
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'active' && (
              <>
                {activeRooms.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeRooms.map((room) => (
                      <RoomCard
                        key={room.id}
                        room={room}
                        onJoin={handleJoinRoom}
                        onDelete={handleDeleteRoom}
                        onCopy={copyMeetingLink}
                        copiedId={copiedRoomId}
                        isActive={true}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={PhoneCall}
                    title="No active meetings"
                    description="Start a new meeting to begin collaborating"
                    buttonText="Start Meeting"
                    onButtonClick={() => setShowCreateRoom(true)}
                  />
                )}
              </>
            )}

            {activeTab === 'available' && (
              <>
                {availableRooms.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableRooms.map((room) => (
                      <RoomCard
                        key={room.id}
                        room={room}
                        onJoin={handleJoinRoom}
                        onDelete={handleDeleteRoom}
                        onCopy={copyMeetingLink}
                        copiedId={copiedRoomId}
                        isActive={false}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={Video}
                    title="No available rooms"
                    description="Create a room to have it ready for your team"
                    buttonText="Create Room"
                    onButtonClick={() => setShowCreateRoom(true)}
                  />
                )}
              </>
            )}

            {activeTab === 'history' && (
              <>
                {loadingHistory ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-virgo-teal-300 flex items-center gap-3">
                      <div className="w-8 h-8 border-2 border-virgo-teal-500 border-t-transparent rounded-full animate-spin" />
                      Loading history...
                    </div>
                  </div>
                ) : pastRooms.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pastRooms.map((room) => (
                      <div
                        key={room.id}
                        className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-2xl border border-virgo-navy-600 p-6"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-virgo-navy-600 to-virgo-navy-700 flex items-center justify-center">
                            <Video className="w-6 h-6 text-virgo-teal-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white">{room.name}</h3>
                            {room.description && (
                              <p className="text-sm text-virgo-teal-400 line-clamp-1">{room.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-virgo-teal-300">
                          <div className="flex items-center gap-1.5">
                            <Users className="w-4 h-4" />
                            <span>{room.participant_count || 0} participants</span>
                          </div>
                          {room.ended_at && (
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-4 h-4" />
                              <span>{new Date(room.ended_at).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={History}
                    title="No past meetings"
                    description="Your meeting history will appear here"
                    buttonText="Start Meeting"
                    onButtonClick={() => setShowCreateRoom(true)}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Create Room Modal */}
      {showCreateRoom && (
        <CreateRoomModal
          onClose={() => setShowCreateRoom(false)}
          onCreate={handleCreateRoom}
        />
      )}
    </div>
  );
};

// Room Card Component
const RoomCard = ({ room, onJoin, onDelete, onCopy, copiedId, isActive }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      className={`relative bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-2xl border transition-all hover:shadow-lg cursor-pointer group ${
        isActive
          ? 'border-cyan-500/30 hover:border-cyan-500/50 shadow-cyan-500/10'
          : 'border-virgo-navy-600 hover:border-virgo-teal-500/50'
      }`}
      onClick={() => onJoin(room)}
    >
      {/* Active Indicator */}
      {isActive && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-virgo-navy-700 animate-pulse" />
      )}

      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isActive
                ? 'bg-gradient-to-br from-cyan-500 to-indigo-600'
                : 'bg-gradient-to-br from-virgo-navy-600 to-virgo-navy-700'
            }`}>
              <Video className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white group-hover:text-cyan-300 transition">
                {room.name}
              </h3>
              {room.description && (
                <p className="text-sm text-virgo-teal-400 line-clamp-1">
                  {room.description}
                </p>
              )}
            </div>
          </div>

          {/* Actions Menu */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-2 rounded-lg text-virgo-teal-400 hover:bg-virgo-navy-600 transition opacity-0 group-hover:opacity-100"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-10 bg-virgo-navy-600 rounded-lg shadow-xl border border-virgo-navy-500 py-1 z-10 min-w-[140px]">
                <button
                  onClick={(e) => {
                    onCopy(room, e);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-virgo-navy-500 transition"
                >
                  {copiedId === room.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {copiedId === room.id ? 'Copied!' : 'Copy Link'}
                </button>
                <button
                  onClick={(e) => {
                    onDelete(room.id, e);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-virgo-navy-500 transition"
                >
                  <Trash2 className="w-4 h-4" />
                  End Meeting
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-virgo-teal-300 mb-4">
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4" />
            <span>{room.participant_count || 0} / {room.max_participants}</span>
          </div>
          {room.require_password && (
            <div className="flex items-center gap-1.5">
              <Shield className="w-4 h-4" />
              <span>Protected</span>
            </div>
          )}
          {room.started_at && (
            <div className="flex items-center gap-1.5 text-green-400">
              <Clock className="w-4 h-4" />
              <span>Live</span>
            </div>
          )}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onJoin(room);
          }}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition ${
            isActive
              ? 'bg-gradient-to-r from-cyan-500 to-indigo-600 text-white hover:from-cyan-600 hover:to-indigo-700'
              : 'bg-virgo-navy-600 text-virgo-teal-300 hover:bg-virgo-navy-500 hover:text-white'
          }`}
        >
          <Play className="w-5 h-5" />
          {isActive ? 'Join Now' : 'Start Meeting'}
        </button>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ icon: Icon, title, description, buttonText, onButtonClick }) => (
  <div className="text-center py-16">
    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 border border-virgo-navy-600 flex items-center justify-center mx-auto mb-6">
      <Icon className="w-10 h-10 text-virgo-teal-500" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-virgo-teal-400 mb-6 max-w-md mx-auto">{description}</p>
    <button
      onClick={onButtonClick}
      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white rounded-xl hover:from-cyan-600 hover:to-indigo-700 transition font-medium"
    >
      <Plus className="w-5 h-5" />
      {buttonText}
    </button>
  </div>
);

export default Visage;
